import { StageInterface } from "@/v2/types/StageInterface";
import { AuditLogInterface } from "@/v2/types/AuditLogInterface";
import { ClassifiedLotsResult } from "./classifyActiveContractLots";
import { getAllLotsForReports } from "./getLotsDataForReports";
import { LotStatus } from "@/enums/LotStatus";

interface ExportSections {
  overview: { selected: boolean };
  breakdown: {
    selected: boolean;
    stages: Record<string, boolean>;
  };
  activity: {
    selected: boolean;
    stages: Record<string, boolean>;
  };
}

interface PrepareExportDataParams {
  estateId: string;
  estateName: string;
  estateLogoUrl?: string;
  sections: ExportSections;
  stages: StageInterface[];
  totalLotsByStatus: Record<string, number>;
  totalRevenuePerStatus: Record<string, number>;
  classifiedActiveContractLots: ClassifiedLotsResult;
  lotsAuditLogs: AuditLogInterface[];
  selectedStageId: { label: string; value: string };
  sumAllStagesRevenue: number;
}

export const prepareExportData = (params: PrepareExportDataParams) => {
  const {
    estateId,
    estateName,
    estateLogoUrl,
    sections,
    stages,
    totalLotsByStatus,
    totalRevenuePerStatus,
    classifiedActiveContractLots,
    lotsAuditLogs,
    selectedStageId,
    sumAllStagesRevenue,
  } = params;

  const exportData: Record<string, unknown> = {
    estateId,
    estateName,
    estateLogoUrl,
    generatedAt: new Date().toISOString(),
    sections: {},
  };

  const sectionsData: Record<string, unknown> = {};

  if (sections.overview.selected) {
    sectionsData.overview = {
      selected: true,
      data: {
        totalSalesValue: sumAllStagesRevenue,
        stages: stages.map((stage) => ({
          stageId: stage._id,
          stageTitle: stage.title,
          completion:
            stage.counts.total === 0
              ? 0
              : Math.round((stage.counts.settled / stage.counts.total) * 100),
          counts: {
            total: stage.counts.total,
            available: stage.counts.available,
            hold: stage.counts.hold,
            underContract: stage.counts.underContract,
            unconditional: stage.counts.unconditional,
            settled: stage.counts.settled,
            notReleased: stage.counts.notReleased,
            offline: stage.counts.offline,
            eoi: stage.counts.eoi,
            option: stage.counts.option,
            buyerSigned: stage.counts.buyerSigned,
            contractExecuted: stage.counts.contractExecuted,
            contractIssued: stage.counts.contractIssued,
          },
          revenue: {
            total: stage.revenue.total,
            available: stage.revenue.available,
            hold: stage.revenue.hold,
            underContract: stage.revenue.underContract,
            unconditional: stage.revenue.unconditional,
            settled: stage.revenue.settled,
            notReleased: stage.revenue.notReleased,
            offline: stage.revenue.offline,
            eoi: stage.revenue.eoi,
            option: stage.revenue.option,
            buyerSigned: stage.revenue.buyerSigned,
            contractExecuted: stage.revenue.contractExecuted,
            contractIssued: stage.revenue.contractIssued,
          },
        })),
        projectTotals: {
          totalLots: totalLotsByStatus.total,
          available: totalLotsByStatus.available,
          underContract:
            totalLotsByStatus.underContract + totalLotsByStatus.unconditional,
          settled: totalLotsByStatus.settled,
        },
        totalsByStatus: {
          total: totalLotsByStatus.total,
          available: totalLotsByStatus.available,
          hold: totalLotsByStatus.hold,
          underContract: totalLotsByStatus.underContract,
          unconditional: totalLotsByStatus.unconditional,
          settled: totalLotsByStatus.settled,
          notReleased: totalLotsByStatus.notReleased,
        },
        revenueByStatus: {
          total: totalRevenuePerStatus.total,
          available: totalRevenuePerStatus.available,
          hold: totalRevenuePerStatus.hold,
          underContract: totalRevenuePerStatus.underContract,
          unconditional: totalRevenuePerStatus.unconditional,
          settled: totalRevenuePerStatus.settled,
          notReleased: totalRevenuePerStatus.notReleased,
        },
      },
    };
  }

  if (sections.breakdown.selected) {
    const selectedStageIds = Object.keys(sections.breakdown.stages).filter(
      (stageId) => sections.breakdown.stages[stageId]
    );

    const filteredStages =
      selectedStageIds.length > 0
        ? stages.filter((stage) => selectedStageIds.includes(stage._id))
        : stages;

    const lotsOnHold = getAllLotsForReports(filteredStages, [
      LotStatus.HOLD,
    ]).map((lot) => {
      const stage = filteredStages.find((s) =>
        s.lots.some((l) => l._id === lot._id)
      );

      const audits = lotsAuditLogs.filter(
        (audit) =>
          audit.entityId === lot._id &&
          (audit.changes?.new as Record<string, string>)?.status ===
            LotStatus.HOLD
      );
      let daysOnHold = 0;

      if (audits.length > 0) {
        const holdStartDate = new Date(audits[0].createdAt);
        const currentDate = new Date();
        const diffInMs = currentDate.getTime() - holdStartDate.getTime();

        daysOnHold = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
      }

      return {
        lotId: lot._id,
        lotNumber: lot.title,
        price:
          lot.price &&
          ["fixed", "from"].includes(lot.price.type?.toLowerCase()) &&
          lot.price.value
            ? lot.price.value
            : null,
        firstBuyer: lot.firstBuyer || "",
        secondBuyer: lot.secondBuyer || "",
        daysOnHold,
        stageId: stage?._id || "",
        stageTitle: stage?.title || "",
      };
    });

    const lotsUnderContract = getAllLotsForReports(filteredStages, [
      LotStatus.UNDER_CONTRACT,
      LotStatus.UNCONDITIONAL,
    ]).map((lot) => {
      const stage = filteredStages.find((s) =>
        s.lots.some((l) => l._id === lot._id)
      );

      return {
        lotId: lot._id,
        lotNumber: lot.title,
        price:
          lot.price &&
          ["fixed", "from"].includes(lot.price.type?.toLowerCase()) &&
          lot.price.value
            ? lot.price.value
            : null,
        firstBuyer: lot.firstBuyer || "",
        secondBuyer: lot.secondBuyer || "",
        status: lot.status,
        exchangedDate: lot.exchangedDate || null,
        deposit: lot.deposit || null,
        conditions: lot.conditions || null,
        financeDueDate: lot.financeDueDate || null,
        stageId: stage?._id || "",
        stageTitle: stage?.title || "",
      };
    });

    sectionsData.breakdown = {
      selected: true,
      selectedStages: selectedStageIds,
      data: {
        selectedStageId: selectedStageId.value,
        selectedStageName: selectedStageId.label,
        stages: filteredStages.map((stage) => {
          const stageLotsOnHold = lotsOnHold.filter(
            (lot) => lot.stageId === stage._id
          );

          const stageLotsUnderContract = lotsUnderContract.filter(
            (lot) => lot.stageId === stage._id
          );

          return {
            stageId: stage._id,
            stageTitle: stage.title,
            completion:
              stage.counts.total === 0
                ? 0
                : Math.round((stage.counts.settled / stage.counts.total) * 100),
            counts: {
              total: stage.counts.total,
              available: stage.counts.available,
              hold: stage.counts.hold,
              underContract: stage.counts.underContract,
              unconditional: stage.counts.unconditional,
              settled: stage.counts.settled,
              notReleased: stage.counts.notReleased,
              offline: stage.counts.offline,
              eoi: stage.counts.eoi,
              option: stage.counts.option,
              buyerSigned: stage.counts.buyerSigned,
              contractExecuted: stage.counts.contractExecuted,
              contractIssued: stage.counts.contractIssued,
            },
            revenue: {
              total: stage.revenue.total,
              available: stage.revenue.available,
              hold: stage.revenue.hold,
              underContract: stage.revenue.underContract,
              unconditional: stage.revenue.unconditional,
              settled: stage.revenue.settled,
              notReleased: stage.revenue.notReleased,
              offline: stage.revenue.offline,
              eoi: stage.revenue.eoi,
              option: stage.revenue.option,
              buyerSigned: stage.revenue.buyerSigned,
              contractExecuted: stage.revenue.contractExecuted,
              contractIssued: stage.revenue.contractIssued,
            },
            lotsOnHold: stageLotsOnHold,
            lotsUnderContract: stageLotsUnderContract,
          };
        }),
        statusCards: {
          available: {
            count:
              selectedStageId.value === "all"
                ? totalLotsByStatus.available
                : filteredStages.reduce(
                    (sum, stage) => sum + stage.counts.available,
                    0
                  ),
            value:
              selectedStageId.value === "all"
                ? totalRevenuePerStatus.available
                : filteredStages.reduce(
                    (sum, stage) => sum + stage.revenue.available,
                    0
                  ),
          },
          hold: {
            count:
              selectedStageId.value === "all"
                ? totalLotsByStatus.hold
                : filteredStages.reduce(
                    (sum, stage) => sum + stage.counts.hold,
                    0
                  ),
            value:
              selectedStageId.value === "all"
                ? totalRevenuePerStatus.hold
                : filteredStages.reduce(
                    (sum, stage) => sum + stage.revenue.hold,
                    0
                  ),
          },
          underContract: {
            quantity:
              selectedStageId.value === "all"
                ? classifiedActiveContractLots.all.underContract.quantity
                : filteredStages.reduce(
                    (sum, stage) =>
                      sum +
                      (classifiedActiveContractLots[stage._id]?.underContract
                        .quantity || 0),
                    0
                  ),
            value:
              selectedStageId.value === "all"
                ? classifiedActiveContractLots.all.underContract.value
                : filteredStages.reduce(
                    (sum, stage) =>
                      sum +
                      (classifiedActiveContractLots[stage._id]?.underContract
                        .value || 0),
                    0
                  ),
          },
          unconditional: {
            quantity:
              selectedStageId.value === "all"
                ? classifiedActiveContractLots.all.unconditional.quantity
                : filteredStages.reduce(
                    (sum, stage) =>
                      sum +
                      (classifiedActiveContractLots[stage._id]?.unconditional
                        .quantity || 0),
                    0
                  ),
            value:
              selectedStageId.value === "all"
                ? classifiedActiveContractLots.all.unconditional.value
                : filteredStages.reduce(
                    (sum, stage) =>
                      sum +
                      (classifiedActiveContractLots[stage._id]?.unconditional
                        .value || 0),
                    0
                  ),
          },
          settled: {
            count:
              selectedStageId.value === "all"
                ? totalLotsByStatus.settled
                : filteredStages.reduce(
                    (sum, stage) => sum + stage.counts.settled,
                    0
                  ),
            value:
              selectedStageId.value === "all"
                ? totalRevenuePerStatus.settled
                : filteredStages.reduce(
                    (sum, stage) => sum + stage.revenue.settled,
                    0
                  ),
          },
        },
      },
    };
  }

  if (sections.activity.selected) {
    const selectedStageIds = Object.keys(sections.activity.stages).filter(
      (stageId) => sections.activity.stages[stageId]
    );

    sectionsData.activity = {
      selected: true,
      selectedStages: selectedStageIds,
      data: {
        period: "30days",
        customDateFrom: null,
        customDateTo: null,
        metrics: {},
        activityBreakdown: [],
      },
    };
  }

  exportData.sections = sectionsData;

  return exportData;
};
